// apps/merchant-v3/src/components/topbar/OutstandingInvoiceWidget.tsx
//
// Phase 6 Wave 12 Task 42 — TopBar widget rendered for INVOICE-mode
// merchants in place of BalanceChip (conditional render).
// Shows "Outstanding: $X" with a red `(N overdue)` badge when at least
// one invoice is past due. Click navigates to
// /billing/invoices?status=overdue so the merchant lands on the
// filtered list. Reads `useBillingSummary().outstanding` +
// `overdue_count`.
//
// Distinct from the legacy `components/OutstandingChip.tsx` shim, which
// shows the cents-level KPI; this widget exposes the merchant-major-units
// total + overdue badge per the Wave 12 design + carries the new
// data-testid `topbar-outstanding-widget`.
//
// vg-binding: PLAN:task-42
// vg-binding: P6.D-114
// vg-binding: P6.D-245
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import { Link } from 'react-router-dom';
import { FileWarning } from 'lucide-react';

import { useTranslation } from '@/lib/i18n';
import { useBillingSummary } from '@/hooks/useBillingSummary';
import { useWalletCredit } from '@/pages/wallet/hooks/useWallet';
import { useUser } from '@/store/user';
import { cn } from '@/lib/cn';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function OutstandingInvoiceWidget(): JSX.Element {
  const { t } = useTranslation('common');
  const summary = useBillingSummary();
  const creditQ = useWalletCredit();
  const user = useUser();

  const currency = summary?.currency ?? user?.displayCurrency ?? 'USD';
  // Unify with the /wallet "Outstanding" card: both must read the SAME
  // canonical credit debt (getMerchantCreditSummary().used surfaced via
  // useWalletCredit), not two different metrics. Previously this chip showed
  // invoice net-outstanding while /wallet showed credit debt → the two never
  // matched (dbg-20260721). The overdue-invoice badge stays sourced from the
  // billing summary as a supplementary signal.
  const outstandingUsed = creditQ.data?.used;
  const outstandingLabel =
    typeof outstandingUsed === 'number'
      ? formatMoney(outstandingUsed, currency)
      : '—';
  const overdueCount = summary?.overdue_count ?? 0;
  const hasOverdue = overdueCount > 0;

  return (
    <Link
      to="/billing/invoices?status=overdue"
      data-testid="topbar-outstanding-chip"
      aria-label={`Outstanding ${outstandingLabel} ${
        hasOverdue ? `(${overdueCount} overdue)` : ''
      }`}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        hasOverdue
          ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100'
          : 'border-pw-border bg-pw-card text-pw-text-muted hover:bg-pw-surface',
      )}
    >
      <FileWarning
        className={cn(
          'h-4 w-4',
          hasOverdue ? 'text-red-600' : 'text-pw-text-muted',
        )}
        aria-hidden
      />
      <span className="text-[10px] uppercase text-pw-text-muted">
        {t('topbar.outstanding.label', { defaultValue: 'Outstanding' })}
      </span>
      <span>{outstandingLabel}</span>
      {hasOverdue ? (
        <span
          data-testid="topbar-outstanding-overdue-badge"
          className="ml-1 inline-flex h-5 items-center rounded-full bg-red-600 px-2 text-[10px] font-semibold text-white"
        >
          {t('topbar.outstanding.overdueBadge', {
            count: overdueCount,
            defaultValue: '{{count}} overdue',
          })}
        </span>
      ) : null}
    </Link>
  );
}

export default OutstandingInvoiceWidget;
